import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as Yup from 'yup';
import { createUppy, useUppy } from '@/lib/uppy';
import Icon from '@/components/Icon';
import Button from '@/components/Button';
import LoadingButton from '@/components/LoadingButton';
import Popover from '@/components/Popover';
import HookForm from '@/components/HookForm';
import HookProductsFields, {
  defaultValues as productDefaultValues,
  getInitialValues as getProductsInitialValues,
  schema as productsSchema
} from '@/components/Inputs/HookProductsFields';
import { JSON_EXTENSION, JSON_TYPE, generateULID } from '@/utils';

const propTypes = {
  products: PropTypes.array,
  dealId: PropTypes.number,
  isEdit: PropTypes.bool,
  onSubmit: PropTypes.func,
  disabled: PropTypes.bool,
  editButtonClassName: PropTypes.string
};

const defaultProps = {
  products: [],
  dealId: null,
  isEdit: false,
  onSubmit: () => {},
  disabled: false,
  editButtonClassName: ''
};

const ZOOM_IN_DURATION = 200;

const schema = Yup.object().shape({
  entity_products: productsSchema
});

const defaultValues = {
  entity_products: [productDefaultValues]
};

function ProductsFormPopover({
  products, dealId, isEdit, onSubmit, disabled, editButtonClassName
}) {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isZooming, setIsZooming] = useState(false);

  const cancelButtonRef = useRef(null);
  const popoverRef = useRef(false);

  useEffect(() => {
    const handlePopoverClick = (event) => {
      const popoverNode = popoverRef.current;
      const productSelectNode = document.querySelector('.select__control--is-focused');

      if (popoverNode && typeof popoverNode.contains === 'function') {
        const clickedInside = popoverNode.contains(event.target) || Boolean(productSelectNode);

        if (!clickedInside) {
          setIsZooming(true);
          setTimeout(() => setIsZooming(false), ZOOM_IN_DURATION);
        }
      }
    };

    document.addEventListener('mousedown', handlePopoverClick);
    return () => {
      document.removeEventListener('mousedown', handlePopoverClick);
    };
  }, [popoverRef]);

  const uppy = useUppy(() => {
    const newUppy = createUppy({
      allowedFileTypes: [JSON_EXTENSION],
      maxFileSizeMega: 50,
      maxNumberOfFiles: 100
    });

    return newUppy;
  });

  /*
   * A biblioteca react-hook-form que é utilizada para gerenciar o estado do
   * formulário utiliza apenas defaultValues para definir os valores iniciais.
   * Por isso precisou-se de um tratamento para incluir tanto os valores
   * iniciais quanto os default.
   * ref: https://react-hook-form.com/docs/useform?#defaultValues
   */
  const initialValues = useMemo(() => {
    const productsInitialValues = getProductsInitialValues(products);
    return {
      entity_products: productsInitialValues,
      ...(isEdit ? productsInitialValues : defaultValues)
    };
  }, [products, dealId, isEdit]);

  const uploadProductsMetadata = async (items = []) => {
    const productList = items.map((product) => {
      const { description, _destroy } = product;
      if (_destroy) {
        return product;
      }

      const metadataId = generateULID();
      uppy.addFile({
        name: `metadata-${metadataId}.json`,
        type: JSON_TYPE,
        data: new Blob([JSON.stringify({ description })], { type: JSON_TYPE })
      });
      return { ...product, metadataId };
    });

    const result = await uppy.upload();
    const uploads = result.successful?.map((file) => file.response.body);

    return productList.map(({ metadataId, ...product }) => ({
      ...product,
      metadata: metadataId
        ? uploads.find(({ metadata }) => metadata.filename.includes(metadataId))
        : null
    }));
  };

  const handleFormSubmit = async (params) => {
    setIsSubmitting(true);

    const formData = {
      ...params,
      entity_products: await uploadProductsMetadata(params?.entity_products)
    };

    onSubmit(
      { ...formData, value: formData?.calculated_value },
      () => {
        uppy.reset();
        cancelButtonRef?.current?.click?.();
        setShowModal(false);
        setIsSubmitting(false);
      }
    );
  };

  return (
    <Popover
      flip
      showArrow
      hide={!showModal}
      className='max-width-7 max-height-6 z-index-1060'
      placement={isEdit ? 'left-start' : 'top-end'}
      rootClose={false}
      content={({ onHide }) => (
        <div
          ref={popoverRef}
          className='mt-2'
          style={{
            transform: isZooming ? 'scale(1.02)' : 'scale(1)',
            transition: 'transform 0.2s ease-in-out'
          }}
        >
          <h3>Adicionar produtos e serviços</h3>
          <HookForm
            name={`create-deal-${dealId}-products`}
            onSubmit={handleFormSubmit}
            defaultValues={{ ...initialValues }}
            validationSchema={schema}
          >
            {() => (
              <>
                <div
                  className='deal-products-height d-flex my-2 overflow-y-scroll'
                  onMouseEnter={() => setIsZooming(false)}
                >
                  <HookProductsFields
                    downloadMetadata
                    feature='deal'
                    isOverlayed
                  />
                </div>
                <hr />
                <div className='d-flex justify-content-end mt-2'>
                  <Button
                    variant='link'
                    className='me-2 fw-bold'
                    ref={cancelButtonRef}
                    onClick={onHide}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <LoadingButton
                    type='submit'
                    isLoading={isSubmitting}
                    variant='primary'
                  >
                    Salvar
                  </LoadingButton>
                </div>
              </>
            )}
          </HookForm>
        </div>
      )}
    >
      <div className={classnames({ 'mt-n2 ms-n2': isEdit })}>
        <Button
          variant={isEdit ? 'link' : 'light'}
          className={classnames(
            'py-1 fw-bold text-primary',
            isEdit ? editButtonClassName : ''
          )}
          size='md'
          disabled={disabled}
          onClick={() => setShowModal(true)}
        >
          <Icon name={isEdit ? 'edit-field' : 'plus'} size='md' className='me-1' />
          {isEdit ? '' : 'Adicionar produtos/serviços'}
        </Button>
      </div>
    </Popover>
  );
}

ProductsFormPopover.propTypes = propTypes;
ProductsFormPopover.defaultProps = defaultProps;

export default ProductsFormPopover;
